/**
 * Recipe → sequence templates — the PURE bridge between the Email Marketing
 * recipe gallery and the outreach sequence engine.
 *
 * The recipe definitions (Welcome series, Re-engage lost lead, Renewal
 * reminder, Weekly customer update) live in src/lib/crm/emailRecipes.ts as
 * governed `AutomationRecipe`s — ordered cadence steps with `{{firstName}}`-
 * style tokens and honest, non-slop copy grounded in each recipe's purpose.
 * `recipeToSequenceInput` there compiles one into the exact shape
 * `saveSequence` persists.
 *
 * This module adds the two pure helpers the "Use this recipe" click leans on:
 *
 *   • `findSequenceByName` — dedupe by the recipe's prefixed name so a second
 *     click OPENS the existing sequence instead of creating a twin.
 *   • `validateRecipeTemplate` — a well-formedness contract the tests assert:
 *     every step has a non-negative integer offset, a non-empty subject and
 *     body, and only valid `{{token}}` names. Never mutates its input.
 *
 * Deliberately PURE: no store, no clocks, no network — a mapping the UI and
 * tests can trust byte-for-byte.
 */

import type { AutomationRecipe, RecipeSequenceInput } from './emailRecipes';
import { recipeToSequenceInput } from './emailRecipes';

/** The token names a recipe body/subject may reference (case-insensitive). */
export const RECIPE_TOKENS = ['firstName', 'name', 'company'] as const;
export type RecipeToken = (typeof RECIPE_TOKENS)[number];

const TOKEN_RE = /\{\{\s*([a-zA-Z]+)\s*\}\}/g;
const VALID_TOKENS = new Set<string>(RECIPE_TOKENS.map((t) => t.toLowerCase()));

/** Every `{{token}}` used in `text` that is not one we can fill. */
export function unknownTokens(text: string): string[] {
  const bad: string[] = [];
  for (const m of text.matchAll(TOKEN_RE)) {
    const name = m[1].toLowerCase();
    if (!VALID_TOKENS.has(name) && !bad.includes(m[1])) bad.push(m[1]);
  }
  return bad;
}

/**
 * Human-readable problems that would make a recipe template unsafe to ship
 * or to compile into a sequence. An empty array means the template is
 * well-formed. Pure: `recipe` is never mutated.
 */
export function validateRecipeTemplate(recipe: AutomationRecipe): string[] {
  const issues: string[] = [];
  if (recipe.steps.length < 3 || recipe.steps.length > 5) {
    issues.push(`${recipe.id}: expected 3–5 steps, got ${recipe.steps.length}.`);
  }
  const seenIds = new Set<string>();
  recipe.steps.forEach((step, i) => {
    const where = `${recipe.id} step ${i + 1}`;
    if (seenIds.has(step.id)) issues.push(`${where}: duplicate step id "${step.id}".`);
    seenIds.add(step.id);
    if (!Number.isInteger(step.dayOffset) || step.dayOffset < 0) {
      issues.push(`${where}: dayOffset must be a whole, non-negative number.`);
    }
    const subject = (step.subject ?? '').trim();
    const body = (step.body ?? '').trim();
    if (subject === '') issues.push(`${where}: subject is empty.`);
    if (body === '') issues.push(`${where}: body is empty.`);
    for (const bad of [...unknownTokens(step.subject ?? ''), ...unknownTokens(step.body ?? '')]) {
      issues.push(`${where}: unknown token {{${bad}}}.`);
    }
  });
  // First touch should be same-day so the cadence starts on enrollment.
  if (recipe.steps.length > 0 && recipe.steps[0].dayOffset !== 0) {
    issues.push(`${recipe.id}: first step must be same-day (dayOffset 0).`);
  }
  return issues;
}

/**
 * Find an existing sequence whose name matches (case-insensitively) the name
 * a recipe would create — the dedupe key for the "Use this recipe" click.
 * Returns the matching sequence or null. Pure filter.
 */
export function findSequenceByName<T extends { id: string; name: string }>(
  sequences: T[],
  name: string,
): T | null {
  const key = name.trim().toLowerCase();
  return sequences.find((s) => s.name.trim().toLowerCase() === key) ?? null;
}

/** Re-export the recipe → engine mapping so callers have one import site. */
export function recipeSequenceInput(recipe: AutomationRecipe): RecipeSequenceInput {
  return recipeToSequenceInput(recipe);
}
